import { CoordinatorService } from './coordinator.service';
import { Notepad } from './notepad';
import { NotepadItem } from './notepad-item';
import { Story } from './story';
import { Document } from './document';
import { Chapter } from './chapter';

type TARGET = Notepad | Story | Document;
export class Notepad$ {
    private _targetMap = new Map<string, TARGET>();

    private _current?: TARGET;

    private _lastNotepad?: Notepad;
    private _lastStory?: Story;
    private _lastDocument?: Document;

    private _disabledStoryNotepadSet = new Set<Notepad>();

    private _currentInsertTarget?: TARGET | undefined;
    isCurrentInsertAbove = false;

    constructor(public coords: CoordinatorService) {}

    clear() {
        this._targetMap.clear();
        this._current = undefined;
        this._lastNotepad = undefined;
        this._lastStory = undefined;
        this._lastDocument = undefined;
        this._disabledStoryNotepadSet.clear();
        this._currentInsertTarget = undefined;
        this.isCurrentInsertAbove = false;
    }

    editorSwitch() {
        if (this.coords.isNotepad) {
            if (!this._current || this._current instanceof Notepad) return;
            this.coords.reader$.current = this._current;
        } else {
            if (!this.coords.reader$.current) return;
            this.current = this.coords.reader$.current;
        }
    }
    markForManifestStore() {
        this._targetMap.forEach((ntp) => {
            ntp.markForManifestStore();
        });
    }

    isStoryDisabledNotepad(notepad: Notepad) {
        return this._disabledStoryNotepadSet.has(notepad);
    }

    setStoryDisabledNotepad(notepad: Notepad, value: boolean) {
        if (value) this._disabledStoryNotepadSet.add(notepad);
        else this._disabledStoryNotepadSet.delete(notepad);
    }

    get activeStoryNotepadItemLs() {
        const ls: NotepadItem[] = [];
        this.notepadLs.forEach((np) => {
            if (!this._disabledStoryNotepadSet.has(np)) ls.push(...np.itemLs);
        });
        return ls;
    }

    get displayNotepad() {
        if (!this._targetMap.size) {
            this.create({ title: this.coords.text('Default') });
        }

        if (!this._current) {
            this._current = this._targetMap.values().next().value;
        }
        return this._current!;
    }

    publish() {
        if (this._current instanceof Story) this.coords.hub$.publishDialog(this._current);
    }

    async export() {
        if (this._current instanceof Story) {
            await this._current.hubExport({ isLocalSave: true });
        }
    }

    load3(ls: any) {
        const docLs: any[] = [];
        ls?.forEach((x: any) => {
            try {
                let target: TARGET;
                if (x.isStory) target = new Story(this.coords, x);
                else if (x.isDocument) {
                    docLs.push(x);
                    return;
                } else target = new Notepad(this.coords, x);

                this._targetMap.set(target.id, target);
            } catch (e) {
                this.coords.utils$.consoleError('NOTEPAD LOAD3 ERROR', e);
            }
        });

        docLs.forEach((x) => {
            const target = new Document(this.coords, x);
            this._targetMap.set(target.id, target);
        });
    }

    start() {
        if (this._current) this.coords.isNotepad = true;
        else this.listDialog();

        this.coords.resultList$.markForCheck();
    }

    create(params: any = {}) {
        let n: Notepad | Story | Document;
        if (params.isNewStory) n = new Story(this.coords, params);
        else if (params.isNewDocument) {
            n = new Document(this.coords, params);
        } else n = new Notepad(this.coords, params);

        this._targetMap.set(n.id, n);
        this.coords.display$.markForCheck();
        n.markForManifestStore();
        return n;
    }

    delete(arg: TARGET) {
        this._targetMap.delete(arg.id);
        this.coords.display$.markForCheck();
    }

    getInstance(x: { id: string; parentId?: string; type: string }) {
        if (x.parentId) {
            return this._targetMap.get(x.parentId)?.get(x.id);
        } else return this._targetMap.get(x.id);
    }

    menuClick(params: any) {
        const ntp = this.current as Notepad;
        switch (params.type) {
            case 'addToStory':
                if (params.story === '__new__')
                    return this.dialog('new-story', {
                        fun: (st: Story) => st.addItemFromNotepadItem(params.itm),
                    });

                params.story.addItemFromNotepadItem(params.itm);
                break;
            case 'newNote':
                if (!this.coords.utils$.htmlTextContent(params.selectedText)) return;
                const note = ntp.addItem();
                note.body += `<p>${params.selectedText}</p>`;
                ntp.currentItem = note;
                break;
            case 'appendText':
                if (!this.coords.utils$.htmlTextContent(params.selectedText)) return;
                ntp.currentItem.body += `<p>${params.selectedText}</p>`;
                break;
            default:
                const ntpItm = ntp.currentItem;
                ntpItm.evidence.addQuote(params.type, params.itm, params.selectedText);
        }

        this.coords.display$.markForCheck();
    }

    get storyLs(): Story[] {
        return Array.from(this._targetMap.values()).filter((n) => n instanceof Story) as Story[];
    }

    get notepadLs(): Notepad[] {
        const ls: Notepad[] = Array.from(this._targetMap.values()).filter(
            (n) => n instanceof Notepad,
        ) as Notepad[];
        if (!ls.length) {
            ls.push(this.create({ title: 'Default' }) as Notepad);
        }
        return ls;
    }

    notepadItemsFromId(ls: string[]) {
        const finalLs: NotepadItem[] = [];
        this.notepadLs.forEach((np) => {
            ls.forEach((ni) => {
                const itm = np.get(ni);
                if (itm) finalLs.push(itm);
            });
        });

        return finalLs;
    }

    get documentLs(): Document[] {
        return Array.from(this._targetMap.values()).filter(
            (n) => n instanceof Document,
        ) as Document[];
    }

    get(notepadId: string) {
        return this._targetMap.get(notepadId);
    }

    getByTitle(title: string) {
        title = this.coords.utils$.normalizeString(title);
        for (const n of this._targetMap.values()) {
            if (this.coords.utils$.normalizeString(n.title) === title) return n;
        }
        return;
    }

    activate() {
        if (this._current) return this._current.activate();
        const stLs = this.storyLs;
        if (stLs.length) {
            if (this._lastStory) this._lastStory.activate();
            else stLs[0].activate();
        }
        if (this._lastNotepad) this._lastNotepad.activate();
        else this.notepadLs[0].activate();
    }



    dialog(arg?: TARGET | 'new-story' | 'new-document', params?: any) {
        let dct: any = {
            action: 'notepad',
        };

        if (arg) {
            if (arg instanceof Notepad) dct.notepad = arg;
            else if (arg instanceof Story) dct.story = arg;
            else if (arg === 'new-story') dct.isNewStory = true;
            else if (arg === 'new-document') dct.isNewDocument = true;
        }
        if (params) dct = Object.assign(dct, params);

        this.coords.display$.dialog$.next(dct);
    }

    listDialog() {
        this.coords.display$.dialog$.next({
            action: 'notepad-list',
        });
    }

    insertDialog(params: { target: TARGET; isAbove: boolean; chapter: Chapter }) {
        this.coords.display$.dialog$.next(Object.assign({ action: 'chapter-insert' }, params));
    }

    hasCurrent() {
        return !!this._current;
    }

    public get current(): TARGET {
        if (!this._current) {
            this._current = this.notepadLs[0];
        }
        return this._current;
    }

    public set current(value: TARGET) {
        this._current = value;
        if (this.current) {
            this.coords.isNotepad = true;
            this.coords.isReader = false;
            if (this.current instanceof Notepad) this._lastNotepad = this.current;
            else if (this.current instanceof Story) this._lastStory = this.current;
            else if (this.current instanceof Document) this._lastDocument = this.current;
        }
        this.coords.display$.markForCheck();
    }

    last(tp: 'notepad' | 'story' | 'document') {
        switch (tp) {
            case 'notepad':
                return this._lastNotepad || this.notepadLs[0];
            case 'story':
                return this._lastStory || this.storyLs[0];
            case 'document':
                return this._lastDocument || this.documentLs[0];
            default:
                return undefined;
        }
    }

    public get currentInsertTarget(): TARGET | undefined {
        return this._currentInsertTarget;
    }

    setCurentInsertTarget(value: TARGET | undefined, isAbove = false) {
        this._currentInsertTarget = value;
        this.isCurrentInsertAbove = isAbove;
        this.coords.display$.markForCheck();
    }
}
